import { DateRange, DateRangePicker, DefinedRange } from 'mui-daterange-picker';
import React, { useState, useEffect, useMemo } from 'react';
import { CalendarIcon, DateRangeInput, DateRangeInputWrapper, DateRangeWrapper,
  DateTimeRangeWrapper, FooterWrapper, ModalWrapper, TimePickerWrapper } from './DateTimeRangePicker.styled';
import enUsLocale from 'date-fns/locale/en-US';
import AMPMSelect from './AMPMSlide';
import { CustomButton, CustomOutlinedButton } from '../Button';
import { ReactComponent as CheckCircle } from '../../../assets/icons/base/check-circle.svg';
import { ReactComponent as Close } from '../../../assets/img/icons/close.svg';
import {
  addDays,
  startOfWeek,
  endOfWeek,
  addWeeks,
  startOfMonth,
  endOfMonth,
  addMonths,
  startOfDay,
  endOfDay,
} from 'date-fns';
import moment from 'moment';
import TimePicker from './TimePicker';
import { yearSelectors } from './const';

interface IDateTimeRangePicker {
  minValue?: Date;
  maxValue?: Date;
  maxDate: Date;
  onChange: (val: [Date, Date]) => void;
}


const getTwelveHour = (val: number) => val > 12 ? val - 12 : val;

const getTime = (hours: number, minutes: number) => `${getTwelveHour(hours) < 10 ? '0' : ''}${getTwelveHour(hours)}:${minutes < 10 ? '0' : ''}${minutes}`;

const DateTimeRangePicker = ({ minValue, maxDate, maxValue, onChange }: IDateTimeRangePicker) => {
  const [open, setOpen] = useState(false);
  const minHours = useMemo(() => minValue?.getHours() || 0, [minValue]);
  const maxHours = useMemo(() => maxValue?.getHours() || 0, [maxValue]);
  const [dateRange, setDateRange] = useState<any>({});
  const [time, setTime] = useState({ start: '', end: '' });
  const toggle = () => setOpen(!open);
  useEffect(() => {
    if (maxHours && minHours && minValue && maxValue) {
      setTime({
        start: getTime(minHours, minValue.getMinutes()),
        end: getTime(maxHours, maxValue.getMinutes()),
      });
      setAmStartDay(minHours < 12);
      setAmEndDay(maxHours < 12);
    }
  }, [maxHours, minHours, minValue, maxValue]);
  const [dateRangeToReturn, setDateRangeToReturn] = useState<[Date?, Date?]>([minValue, maxValue]);
  useEffect(() => {
    setDateRange({ startDate: minValue, endDate: maxValue });
    setDateRangeToReturn([minValue, maxValue]);
  }, [minValue, maxValue]);
  const [amStartDay, setAmStartDay] = useState<boolean>(minHours < 12);
  const [amEndDay, setAmEndDay] = useState<boolean>(maxHours < 12);
  // eslint-disable-next-line consistent-return
  const timeToDisplay = useMemo(() => {
    if (dateRangeToReturn?.length >= 2) {
      const firstDay = moment(dateRangeToReturn[0]).format('MM/DD/YY, hh:mm A');
      const lastDay = moment(dateRangeToReturn[1]).format('MM/DD/YY, hh:mm A');
      return `${firstDay} - ${lastDay}`;
    }
  }, [dateRangeToReturn]);

  const handleSubmit = () => {
    if (dateRange?.startDate && dateRange?.endDate) {
      const { startDate, endDate } = dateRange;
      const firstDate = new Date(`${startDate.toDateString()} ${time.start || '0:00'} ${amStartDay ? 'AM' : 'PM'}`);
      const secondDate = new Date(`${endDate.toDateString()} ${time.end || '0:00'} ${amEndDay ? 'AM' : 'PM'}`);
      if (firstDate > secondDate) {
        setDateRangeToReturn([firstDate, firstDate]);
        onChange([firstDate, firstDate]);
      } else {
        setDateRangeToReturn([firstDate, secondDate]);
        onChange([firstDate, secondDate]);
      }
      setOpen(false);
    }
  };
  const handleClose = () => {
    setOpen(false);
    if (!dateRangeToReturn) {
      setAmEndDay(true);
      setAmStartDay(true);
      setDateRange({});
      setTime({ start: '', end: '' });
    }
  };
  const modalRef = React.useRef(null);

  const filterYears = () => {
    const currentYear = (new Date()).getFullYear();
    yearSelectors.forEach((yS) => {
      document.querySelector(yS.selector)?.addEventListener('click', () => {
        document.querySelector(yS.li)?.querySelectorAll('li').forEach((ele) => {
          const eleYear = Number(ele.getAttribute('data-value'));
          if (currentYear !== eleYear && currentYear - 1 !== eleYear) {
            ele.style.display = 'none';
          }
        });
      });
    });
  };

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (
        modalRef.current
        && !(modalRef.current as any).contains(event.target)
        && open
      ) {
        handleClose();
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    if (open) filterYears();
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modalRef.current, open]);

  const handleRangeChange = (range: DateRange) => {
    const start = moment(range?.startDate).format('hh:mm,A');
    const end = moment(range?.endDate).format('hh:mm,A');
    setAmStartDay(start.split(',')[1] === 'AM');
    setAmEndDay(end.split(',')[1] === 'AM');
    setTime({
      start: start.split(',')[0],
      end: end.split(',')[0],
    });
    if (range?.startDate && range?.startDate <= maxDate) {
      setDateRange(range);
    }
  };
  return (
    <DateRangeWrapper id="datetime-range-input">
      <DateRangeInputWrapper id="datetime-range-input" onClick={toggle}>
        <DateRangeInput
          value={
            Object.keys(dateRangeToReturn).length >= 2
              ? timeToDisplay
              : 'Select a Date Range'
          }
          title={
            Object.keys(dateRangeToReturn).length >= 2
              ? timeToDisplay
              : 'Select a Date Range'
          }
        />
        <CalendarIcon />
      </DateRangeInputWrapper>
      {open && (
        <ModalWrapper id="datetime-range-modal" ref={modalRef}>
          <DateTimeRangeWrapper>
            <DateRangePicker
              initialDateRange={dateRange}
              wrapperClassName="date-range-picker-wrapper"
              open={open}
              closeOnClickOutside={true}
              toggle={toggle}
              definedRanges={getCustomRanges(new Date())}
              minDate={new Date(2022, 0, 1)}
              maxDate={addMonths(new Date(), 12)}
              onChange={handleRangeChange}
              locale={enUsLocale}
            />
            <TimePickerWrapper>
              <span>Time</span>
              <TimePicker
                value={time.start}
                onChange={(e) => {
                  setTime({ ...time, start: e });
                }}
              />
              <AMPMSelect onChange={setAmStartDay} value={amStartDay} />
              <span> - </span>
              <TimePicker
                value={time.end}
                onChange={(e) => {
                  setTime({ ...time, end: e });
                }}
              />
              <AMPMSelect onChange={setAmEndDay} value={amEndDay} />
            </TimePickerWrapper>
            <FooterWrapper>
              <CustomOutlinedButton
                onClick={handleClose}
                fullWidth
                outlineColor="primary"
                startIcon={<Close style={{ height: '16px', width: '16px' }} />}
                sx={{
                  height: '32px',
                  color: 'red',
                  borderRadius: '8px !important',
                }}
              >
                Cancel
              </CustomOutlinedButton>
              <CustomButton
                onClick={handleSubmit}
                fullWidth
                bgcolor="primary"
                startIcon={
                  <CheckCircle style={{ height: '16px', width: '16px' }} />
                }
                sx={{
                  height: '32px',
                  borderRadius: '8px !important',
                }}
              >
                Apply
              </CustomButton>
            </FooterWrapper>
          </DateTimeRangeWrapper>
        </ModalWrapper>
      )}
    </DateRangeWrapper>
  );
};

export default DateTimeRangePicker;

export const getCustomRanges = (date: Date, locale?: Locale): DefinedRange[] => [
  {
    label: 'Today',
    startDate: startOfDay(date),
    endDate: endOfDay(date),
  },
  {
    label: 'Yesterday',
    startDate: addDays(startOfDay(date), -1),
    endDate: addDays(endOfDay(date), -1),
  },
  {
    label: 'This Week',
    startDate: startOfWeek(date, { locale }),
    endDate: endOfWeek(date, { locale }),
  },
  {
    label: 'Last Week',
    startDate: startOfWeek(addWeeks(date, -1), { locale }),
    endDate: endOfWeek(addWeeks(date, -1), { locale }),
  },
  {
    label: 'Last 7 Days',
    startDate: addWeeks(date, -1),
    endDate: date,
  },
  {
    label: 'This Month',
    startDate: startOfMonth(date),
    endDate: endOfMonth(date),
  },
  {
    label: 'Last Month',
    startDate: startOfMonth(addMonths(date, -1)),
    endDate: endOfMonth(addMonths(date, -1)),
  },
];
